'use client';
import { Icon } from '@fm-nx/svg';
import Link from 'next/link';
import { useEffect, useState } from 'react';

/**
 * Site-wide announcement banner, driven by the Contentful `announcement`
 * content type. Sits above the navigation and is NOT sticky — it lives in
 * normal document flow at the top of the page.
 *
 * Content and open/close state come from Contentful (fetched in the root
 * layout); this component only handles presentation and per-visitor dismissal.
 */

export type AnnouncementBannerProps = {
    /** Contentful entry id — dismissal is keyed to it, so a newly published announcement re-appears. */
    id: string;
    message: string;
    linkText?: string;
    linkHref?: string;
};

const dismissKey = (id: string) => `fm-announcement-dismissed-${id}`;

const isExternal = (href: string) => /^https?:\/\//i.test(href);

export const AnnouncementBanner = ({ id, message, linkText, linkHref }: AnnouncementBannerProps) => {
    // Render visible on first paint (matches the server HTML, so no layout shift
    // for the common case), then hide after mount if this visitor dismissed it.
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Re-read on every id change so a newly published announcement (new id)
        // shows again even if the previous one was dismissed.
        try {
            setDismissed(window.localStorage.getItem(dismissKey(id)) === 'true');
        } catch {
            // localStorage unavailable (e.g. private mode) — just leave it shown.
            setDismissed(false);
        }
    }, [id]);

    const handleDismiss = () => {
        setDismissed(true);
        try {
            window.localStorage.setItem(dismissKey(id), 'true');
        } catch {
            // Ignore — dismissal simply won't persist across reloads.
        }
    };

    if (!message || dismissed) {
        return null;
    }

    const showLink = Boolean(linkText && linkHref);
    const linkClassName = 'group flex items-center gap-4 whitespace-nowrap';
    const linkContent = (
        <>
            <span className="hidden h-px w-6 bg-gray-900 sm:block" aria-hidden="true" />
            <span className="font-headline text-sm font-bold uppercase tracking-[2.24px]">{linkText}</span>
        </>
    );

    return (
        <div className="w-full bg-[#c7d9f2] text-gray-900" role="region" aria-label="Announcement">
            {/* Match MainNav's container + 14-col grid so the banner content aligns
                with the nav: text to the logo (col 2), actions to the nav's right edge. */}
            <div className="w-full mx-auto max-w-container">
                <div className="mx-6 py-5 md:mx-0 md:py-8">
                    <div className="grid grid-cols-4 md:grid-cols-14">
                        <div className="col-span-4 md:col-span-12 md:col-start-2">
                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
                                <p className="flex-1 font-headline text-base font-bold leading-tight tracking-[-0.32px]">
                                    {message}
                                </p>
                                <div className="flex items-center justify-between gap-6 md:justify-end md:gap-14">
                                    {showLink &&
                                        (isExternal(linkHref as string) ? (
                                            <a
                                                href={linkHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={linkClassName}
                                            >
                                                {linkContent}
                                            </a>
                                        ) : (
                                            <Link href={linkHref as string} className={linkClassName}>
                                                {linkContent}
                                            </Link>
                                        ))}
                                    <button
                                        type="button"
                                        onClick={handleDismiss}
                                        className="flex shrink-0 cursor-pointer items-center"
                                    >
                                        <span className="sr-only">Dismiss announcement</span>
                                        <Icon name="close" color="black" size="base" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementBanner;
